/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { init } from 'echarts';
import { useSelector } from 'react-redux';
import ReactResizeDetector from 'react-resize-detector';
import { contextMenu } from 'react-contexify';

import useChartColors from '../../../hooks/useChartColors';
import getOptions from './TreemapConfig';
import LegendItem from '../legend/LegendItem';
import { getTrendColor } from '../../../utils/chart';
import VisitWebsiteContextMenu from '../../visit-website-context-menu/VisitWebsiteContextMenu';
import withEmptyChart from '../../../hoc/withEmptyChart';

import scss from './Treemap.module.scss';

export const TREEMAP_CONTEXT_MENU = 'treemap/VisitWebsiteContextMenu';

function Treemap({ data, dataKey, showLegend = true, isTrend = false }) {
  const theme = useSelector((state) => state.global.theme);

  const colors = useChartColors();

  const chartContainerRef = useRef(null);
  const [chart, setChart] = useState(null);
  const [activeItems, setActiveItems] = useState([]);

  const toggleItem = useCallback((identification) => {
    setActiveItems((prevItems) =>
      prevItems.map((item) => (item.domain === identification ? { ...item, active: !item.active } : item)),
    );
  }, []);

  useEffect(() => {
    if (chart) {
      const filteredStateData = activeItems.filter((item) => item.active);

      chart.setOption(getOptions(filteredStateData, isTrend, dataKey), {
        notMerge: true,
      });
    }
  }, [chart, activeItems, isTrend, dataKey]);

  useEffect(() => {
    const initialData = data
      .map((domainEntree, i) => ({
        ...domainEntree,
        x: domainEntree.domain,
        y: domainEntree[dataKey].score,
        active: true,
        color: isTrend
          ? getTrendColor({
              trend: domainEntree[dataKey].trend,
              score: domainEntree[dataKey].score,
              theme,
            })
          : colors[i % colors.length],
      }))
      .sort((a, b) => b.y - a.y);

    setActiveItems(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, colors, isTrend, theme]);

  useEffect(() => {
    if (chart) {
      const filteredStateData = activeItems.filter((item) => item.active);

      chart.on('contextmenu', function ({ event, dataIndex }) {
        event.event.preventDefault();

        const data = filteredStateData[dataIndex - 1];

        if (data?.domain) {
          contextMenu.show({
            id: TREEMAP_CONTEXT_MENU,
            event: event.event,
            props: {
              domain: data?.domain,
            },
          });
        }
      });
    }
  }, [chart, activeItems]);

  useEffect(() => {
    const chartInstance = init(chartContainerRef.current);
    setChart(chartInstance);

    return () => {
      chartInstance.dispose();
    };
  }, []);

  return (
    <div>
      <div ref={chartContainerRef} style={{ height: '350px', width: '100%' }} />
      {showLegend && (
        <div className={scss.legend}>
          {activeItems.map(({ active, inFocus, color, domain, label, myCompany }) => (
            <LegendItem
              key={`Treemap-legend-${domain}`}
              title={label || domain}
              isActive={active}
              inFocus={inFocus}
              myCompany={myCompany}
              color={color}
              domain={domain}
              onClick={toggleItem}
            />
          ))}
        </div>
      )}
      <ReactResizeDetector handleWidth onResize={() => chart?.resize()} />

      <VisitWebsiteContextMenu id={TREEMAP_CONTEXT_MENU} />
    </div>
  );
}

export default withEmptyChart(Treemap);
